import { useMobileHeader } from "@/hooks/useMobileHeader";
import {
  ButtonFacetedFilter,
  FacetedFilterOption,
} from "@/components/button-faceted-filter";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeams } from "@/contexts/teams-provider";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useIsSlow } from "@/hooks/useIsSlow";
import { useTranslation } from "@/hooks/useTranslation";
import { getTagsList } from "@/lib/tags";
import {
  CompanyDTO,
  ListingDetailDTO,
  ListingWithCompaniesDTO,
} from "@shaarp/common";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBackOr } from "@/hooks/useBackOr";

export default function ListingContentMobilePage() {
  const { listingId } = useParams();
  // Back to the list the listing was opened from; opened directly, the lists.
  const goBack = useBackOr("/listing");
  const { t } = useTranslation();
  const { teamSelected } = useTeams();

  // A cached read, so a lost network resumes by itself once it is back, as
  // the connection banner says — the read used to happen once, on mount.
  const {
    data: listing,
    isPending,
    isError,
    isFetching,
    refetch,
  } = useApiQuery<ListingDetailDTO>({
    key: ["listing", teamSelected?.id, listingId],
    path: `/listing/${listingId}`,
    enabled: !!listingId && !!teamSelected?.id,
  });
  const isLoading = isPending && !isError;
  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [tagsSelected, setTagsSelected] = useState<string[]>([]);
  const [tagsOptions, setTagsOptions] = useState<FacetedFilterOption[]>([]);

  const companiesListing = listing?.listingType === "companies"
    ? (listing as ListingWithCompaniesDTO)
    : undefined;

  useEffect(() => {
    setCompanies(
      companiesListing?.companies
        ?.filter(
          (c) =>
            tagsSelected.length === 0 ||
            tagsSelected.includes(c.company.assignation?.tag || "")
        )
        .map((c) => c.company) || []
    );
  }, [listing, tagsSelected]);

  useEffect(() => {
    const tagsList = getTagsList(t);
    setTagsOptions(
      tagsList.map((tag) => ({
        label: tag.label,
        value: tag.value,
        total:
          companiesListing?.companies?.filter(
            (c) => c.company.assignation?.tag === tag.value
          ).length || 0,
      }))
    );
  }, [listing, t]);

  const isConnectionSlow = useIsSlow(isLoading);
  useMobileHeader({ title: listing?.title, onBack: goBack, isConnectionSlow });

  return (
    <div
      data-mobile-scroller
      className="overflow-auto overscroll-y-contain absolute top-0 left-0 flex-1 p-2 pt-2 pb-[calc(var(--mobile-bottom-inset)+1rem)] space-y-5 w-full h-full"
    >
      {isLoading ? (
        // In the shape of the company cards that will take its place.
        <div className="space-y-4" aria-busy>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : isError && !listing ? (
        <Empty className="h-full">
          <EmptyHeader>
            <EmptyTitle>{t("components.mobileShell.listingLoadError")}</EmptyTitle>
          </EmptyHeader>
          <EmptyContent>
            <Button
              className="h-11"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {t("components.mobileShell.retry")}
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="space-y-2">
          {listing && (
            <DataTableCompanies
              companies={companies}
              overrideColumnsVisibility={["Tag", "Assignation"]}
              columnsPersistenceKey="data-table-companies-listing-content-mobile-v1"
              enabledListingFeature={true}
              additionalFilters={
                <ButtonFacetedFilter
                  title="Tags"
                  compacted={false}
                  options={tagsOptions}
                  selected={tagsSelected}
                  onChange={setTagsSelected}
                />
              }
              sortingState={
                listing?.ownerType === "shaarp"
                  ? [{ id: "Nom", desc: false }]
                  : [{ id: "Date d'ajout", desc: true }]
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
